import ProductCard from "./ProductCard";
import { Product } from "../../types/ProductType";
import { Branch, Categories } from "../../types/globalTypes";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { useTranslation } from "react-i18next";
import ImportExportProduct from "./ImportExportProduct";
import { useEffect, useRef, useState } from "react";
import { FaMagic } from "react-icons/fa";

type ProductListProps = {
  allCategories: Categories;
  products: Product[];
  allBranches: Branch[];
  setNeedRefetch: React.Dispatch<React.SetStateAction<boolean>>;
};

function ProductsList({
  products,
  allCategories,
  setNeedRefetch,
  allBranches,
}: ProductListProps) {
  const { t } = useTranslation();
  const [moreOptions, setMoreOptions] = useState(false);
  const moreOptionsRef = useRef<HTMLDivElement>(null);
  const [isImportExportModalOpen, setIsImportExportModalOpen] = useState(false);

  function toggleMoreOptions() {
    setMoreOptions(!moreOptions);
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        moreOptionsRef.current &&
        !moreOptionsRef.current.contains(event.target as Node)
      ) {
        setMoreOptions(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      {/* import/export Modal */}
      {isImportExportModalOpen && (
        <ImportExportProduct
          onClose={() => setIsImportExportModalOpen(false)}
          setNeedRefetch={setNeedRefetch}
        />
      )}

      {/* Product List & dropDown Container */}
      <div className="flex flex-col gap-4">
        {/* more options Dropdown */}
        <div
          ref={moreOptionsRef}
          onClick={toggleMoreOptions}
          className="relative w-fit cursor-pointer self-end rounded-sm border border-gray-300 bg-white px-5 py-2 text-sidebar"
        >
          <div className="flex items-center gap-2 font-semibold">
            <FaMagic />
            {t("productList.moreOptions")}
          </div>
          {moreOptions && (
            // import/export
            <div className="absolute start-0 top-[110%] z-[99999] flex min-w-full flex-col overflow-hidden whitespace-nowrap border border-gray-300 bg-white text-sm font-semibold">
              <div
                onClick={() => setIsImportExportModalOpen(true)}
                className={`px-3 py-2 hover:bg-sidebar hover:text-skyblue`}
              >
                {t("products.import_export")}
              </div>
            </div>
            // Select all
          )}
        </div>

        {/* Product List */}
        {products.length > 0 ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-1 sm:gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {products.map((product) => (
              <ProductCard
                allBranchesAvailable={product.allBranchesAvailable}
                branches={product.branches}
                key={product._id}
                _id={product._id}
                calories={product.calories}
                categories={product.categories}
                allCategories={allCategories}
                descriptionAR={product.descriptionAR}
                descriptionEN={product.descriptionEN}
                image={product.image}
                nameAR={product.nameAR}
                nameEN={product.nameEN}
                price={product.price}
                quantity={product.quantity}
                addons={product.addons}
                lowPrice={product.lowPrice}
                lowPriceEndDate={product.lowPriceEndDate}
                options={product.options}
                allBranches={allBranches}
                setNeedRefetch={setNeedRefetch}
                productCost={product.productCost}
                hidden={product.hidden}
                salt={product.salt}
                caffeine={product.caffeine}
              />
            ))}
          </div>
        ) : (
          <>
            <Alert className="border-none bg-skyblue text-sidebar dark:border-none dark:bg-skyblue dark:text-sidebar">
              <AlertTitle className="text-center text-xl">
                {t("productList.noProductsFound")}
              </AlertTitle>
              <AlertDescription className="my-5 text-center font-semibold">
                {t("productList.noProductsMessage")}
              </AlertDescription>
            </Alert>
          </>
        )}
      </div>
    </>
  );
}

export default ProductsList;
